import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { HorizonToastService } from '@bayclubs/horizon';
import { ConfirmationService } from '@bayclubs/utils';

import { ConflictModel, InstructorModel } from '@bayclubs/group-classes-employee-api';
import { SubstitutionBoardEnumerations } from 'src/app/interfaces/interfaces.module';
import { SubBoardApiService } from 'src/app/shared/shared.module';
import { ProposalListItemModel } from '../../models/proposal-list-item-model';
import { SubBoardListItemModel } from '../../models/sub-board-list-item-model';
import { SubBoardListItemModelCacheEntry } from '../../models/sub-board-list-item-model-cache-entry';
import { SubBoardListGatewayApiService } from '../../services/sub-board-list-api-gateway.service';

@Component({
    selector: 'app-sub-board-request-details',
    templateUrl: './sub-board-request-details.component.html',
    styleUrls: ['./sub-board-request-details.component.scss'],
    standalone: false
})
export class SubBoardRequestDetailsComponent implements OnInit {
    public model: SubBoardListItemModel;
    public selectedInstructor: InstructorModel;
    private substitutionRequestId: string;

    constructor(
        activatedRoute: ActivatedRoute,
        private subBoardApi: SubBoardApiService,
        private substitutionRequestApiGateway: SubBoardListGatewayApiService,
        private toast: HorizonToastService,
        private confirmation: ConfirmationService,
        private translate: TranslateService
    ) {
        const queryParams = activatedRoute.snapshot.paramMap;
        this.substitutionRequestId = queryParams.get('id');
        const cacheEntry: SubBoardListItemModelCacheEntry = activatedRoute.snapshot.data.substitutionRequestModel;
        this.model = cacheEntry.model;

        // If data is loaded from cache there is very small chance it might be stale.
        // We will asynchronously load new model, to provide smooth no-latency experience
        // in the most common case of cache representing current state.
        if (cacheEntry.loadedFromCache) {
            const clubId = queryParams.get('clubId');
            this.substitutionRequestApiGateway
                .getSubBoardListItem(clubId, this.substitutionRequestId, false)
                .subscribe(x => (this.model = x));
        }
    }

    ngOnInit() {}

    public onInstructorSelected(instructor: InstructorModel) {
        this.selectedInstructor = instructor;
    }

    public onProposalRejected(proposal: ProposalListItemModel) {
        this.subBoardApi.rejectProposal(this.model.clubId, this.substitutionRequestId, proposal.id).subscribe(() => {
            proposal.status = SubstitutionBoardEnumerations.SubstitutionProposalStatus.rejectedByManager;
            this.model.offersCount--;
            this.showOperationSuccess();
        });
    }

    public onProposalApproved(proposal: ProposalListItemModel) {
        this.substitutionRequestApiGateway
            .conflict(this.model.classId, proposal.providerId)
            .subscribe((conflictModel: ConflictModel) => {
                if (conflictModel.conflictingClass) {
                    const msg = this.translate.instant('SubRequestDetails.CannotApproveProposalConflict');
                    this.toast.error(msg);
                } else {
                    this.executeProposalApproval(proposal);
                }
            });
    }

    private executeProposalApproval(proposal: ProposalListItemModel) {
        this.subBoardApi.approveProposal(this.model.clubId, this.substitutionRequestId, proposal.id).subscribe(() => {
            this.showOperationSuccess();
            proposal.status = SubstitutionBoardEnumerations.SubstitutionProposalStatus.approved;
            this.model.status = SubstitutionBoardEnumerations.SubstitutionRequestStatus.closed;

            // All pending proposals are made cancelled by request
            for (const p of this.model.proposals) {
                if (p.status.code === SubstitutionBoardEnumerations.SubstitutionProposalStatus.pending.code) {
                    p.status = SubstitutionBoardEnumerations.SubstitutionProposalStatus.rejectedByManager;
                }
            }

            // Set substitute info
            this.model.substitutePhotoUrl = this.getRawPhotoUrl(proposal.providerPhotoUrl);
            this.model.substituteFirstName = proposal.providerFirstName;
            this.model.substituteLastName = proposal.providerLastName;
        });
    }

    public onRequestCanceled() {
        this.confirmation
            .show(
                this.translate.instant('SubRequestDetails.CancellationConfirmationTitle'),
                this.translate.instant('SubRequestDetails.CancelSubRequestConfirmation'),
                this.translate.instant('General.Confirm'),
                this.translate.instant('General.Dismiss')
            )
            .subscribe(result => {
                if (result) {
                    this.executeCancel();
                }
            });
    }

    private executeCancel() {
        this.subBoardApi.cancelSubstitutionRequest(this.model.clubId, this.model.id).subscribe(() => {
            this.model.status = SubstitutionBoardEnumerations.SubstitutionRequestStatus.cancelled;
            for (const p of this.model.proposals) {
                if (p.status.code === SubstitutionBoardEnumerations.SubstitutionProposalStatus.pending.code) {
                    p.status = SubstitutionBoardEnumerations.SubstitutionProposalStatus.cancelledByRequest;
                }
            }
        });
    }

    public onConfirmOtherInstructor() {
        this.substitutionRequestApiGateway
            .conflict(this.model.classId, this.selectedInstructor.id)
            .subscribe((conflictModel: ConflictModel) => {
                if (conflictModel.conflictingClass) {
                    const msg = this.translate.instant('SubRequestDetails.CannotApproveInstructorConflict');
                    this.toast.error(msg);
                } else {
                    this.executeConfirmingOtherInstructor();
                }
            });
    }

    private executeConfirmingOtherInstructor() {
        if (this.selectedInstructor) {
            this.subBoardApi
                .confirmInstructorAsSubstitute(this.model.clubId, this.substitutionRequestId, this.selectedInstructor.id)
                .subscribe(() => {
                    this.showOperationSuccess();
                    this.model.status = SubstitutionBoardEnumerations.SubstitutionRequestStatus.closed;
                    this.model.substitutePhotoUrl = this.getRawPhotoUrl(this.selectedInstructor.photoUrl);
                    this.model.substituteFirstName = this.selectedInstructor.name;
                    this.model.substituteLastName = null;
                    for (const p of this.model.proposals) {
                        if (p.status.code === SubstitutionBoardEnumerations.SubstitutionProposalStatus.pending.code) {
                            p.status = SubstitutionBoardEnumerations.SubstitutionProposalStatus.rejectedByManager;
                        }
                    }
                });
        }
    }

    get requestIsOpen() {
        return this.model.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.open.code;
    }

    get requestIsClosed() {
        return this.model.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.closed.code;
    }

    get requestIsCancelled() {
        return this.model.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.cancelled.code;
    }

    get requestIsOverdue() {
        return this.model.status.code === SubstitutionBoardEnumerations.SubstitutionRequestStatus.overdue.code;
    }

    private getRawPhotoUrl(url: string) {
        if (!url) {
            return null;
        }
        return url + '?format=raw&&height=80';
    }

    private showOperationSuccess() {
        this.toast.success(this.translate.instant('Utils.OperationOk'));
    }
}
